import type { Card } from '@campfire/cards';
import type { Seat } from '@campfire/engine';
import type { GinEvent, GinHandResult, GinView } from '@campfire/game-gin';
import { bestArrangement } from '@campfire/melds';
import { cardFace, cardLabel, pickTray, pile } from '../cards.ts';
import { h } from '../dom.ts';
import { name, others, type GameUi, type TableCtx } from '../games.ts';
import { applyOrder, moveBlock, type Move } from '../order.ts';

const SEL = 'gin-sel';
const knockLabel = (rest: readonly Card[]) => {
  const left = bestArrangement(rest).points;
  return left === 0 ? 'Gin!' : `Knock (${left})`;
};
const n = (k: number, what = 'card') => `${k} ${what}${k === 1 ? '' : 's'}`;

/** One meld (or the deadwood) as a tight group of cards. */
function group(cards: readonly Card[], label: string | null, opts: { picked?: readonly string[]; onpick?: (id: string) => void; testid?: string; dead?: boolean } = {}): HTMLElement {
  return h(
    `div.meld-group${opts.dead ? '.dead' : ''}`,
    { 'data-testid': opts.testid },
    label ? h('div.meld-label', {}, label) : null,
    h('div.meld-cards', {}, cards.map((c) => cardFace(c, { picked: opts.picked?.includes(c.id), onclick: opts.onpick ? () => opts.onpick?.(c.id) : undefined }))),
  );
}

/** Both hands laid down after a knock, with layoffs and the score. */
export function ginReveal(ctx: TableCtx, r: GinHandResult): HTMLElement {
  const defender: Seat = 1 - r.knocker;
  const verdict = r.gin
    ? `${name(ctx, r.knocker)} went gin!`
    : r.undercut
      ? `Undercut! ${name(ctx, defender)} had ${r.defenderPoints} vs ${r.knockerPoints}.`
      : `${name(ctx, r.knocker)} knocked with ${r.knockerPoints}; ${name(ctx, defender)} had ${r.defenderPoints}.`;
  return h(
    'div.panel.gin-reveal',
    { 'data-testid': 'gin-reveal' },
    h('strong', {}, `Hand ${r.hand}: ${verdict} ${name(ctx, r.winner)} ${r.winner === ctx.you ? 'score' : 'scores'} ${r.points}.`),
    h('div.muted', {}, `${name(ctx, r.knocker)} (${r.gin ? 'gin' : 'knock'})`),
    h(
      'div.meld-row',
      {},
      r.knockerMelds.map((m, i) =>
        group([...m, ...r.layoffs.filter((l) => l.target === i).flatMap((l) => l.cards)], null),
      ),
      r.knockerDeadwood.length ? group(r.knockerDeadwood, `Deadwood ${r.knockerPoints}`, { dead: true }) : null,
    ),
    r.layoffs.length ? h('div.muted', {}, `${name(ctx, defender)} laid off ${r.layoffs.flatMap((l) => l.cards).map(cardLabel).join(' ')}`) : null,
    h('div.muted', {}, name(ctx, defender)),
    h('div.meld-row', {}, r.defenderMelds.map((m) => group(m, null)), r.defenderDeadwood.length ? group(r.defenderDeadwood, `Deadwood ${r.defenderPoints}`, { dead: true }) : null),
  );
}

export const ginUi: GameUi = {
  id: 'gin',
  privateHands: true,
  seatInfo: (ctx, s) => {
    const v = ctx.msg.view as GinView;
    return `${n(v.handCounts[s] ?? 0)} · ${v.scores[s] ?? 0} pts${s === v.dealer ? ' · dealer' : ''}`;
  },
  render(ctx) {
    const v = ctx.msg.view as GinView;
    const me = ctx.mySeat;
    const raw = v.hand ?? [];
    const sortKey = `keep:gin-sort:${me}`;
    const orderKey = `keep:gin-order:${me}`;
    const sortBy = ctx.local.get<string>(sortKey) ?? 'melds';
    const hand = sortBy === 'custom' ? applyOrder(raw, ctx.local.get<string[]>(orderKey)) : raw;
    const picked = (ctx.local.get<string[]>(SEL) ?? []).filter((id) => raw.some((c) => c.id === id));
    const toggle = (id: string) => ctx.local.set(SEL, picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
    const send = (a: { type: string } & Record<string, unknown>) => {
      ctx.local.set(SEL, []);
      ctx.act(a);
    };
    const move = (m: Move) => {
      ctx.local.set(orderKey, moveBlock(hand.map((c) => c.id), picked, m));
      ctx.local.set(sortKey, 'custom');
    };
    const drawing = ctx.myTurn && v.phase === 'draw';
    const discarding = ctx.myTurn && v.phase === 'discard';
    const one = picked.length === 1 ? (picked[0] as string) : null;
    const ev = (e: GinEvent | null): string => {
      if (!e) return `Hand ${v.handNo}. ${name(ctx, v.turn)} ${v.turn === ctx.you ? 'draw' : 'draws'} first — from the stock or the upcard.`;
      switch (e.kind) {
        case 'draw':
          return `${name(ctx, e.seat)} took ${e.card ? cardLabel(e.card) : 'a card'} from the ${e.from === 'stock' ? 'stock' : 'discard pile'}.`;
        case 'discard':
          return `${name(ctx, e.seat)} discarded ${cardLabel(e.card)}.`;
        case 'washed':
          return `Hand ${e.hand} washed out — nobody knocked before the stock ran down. Same dealer deals again.`;
        case 'hand':
          return v.result ? 'Game over.' : `New hand: ${name(ctx, v.turn)} ${v.turn === ctx.you ? 'draw' : 'draws'} first.`;
      }
    };
    const a = v.arrangement;
    const top = v.discard[0];
    return h(
      'div.gin-table',
      {},
      others(ctx, v.handCounts),
      h(
        'div.row.table-piles',
        {},
        h(
          `${drawing ? 'button' : 'div'}.ocean`,
          { 'data-testid': 'gin-stock', onclick: drawing ? () => send({ type: 'draw', from: 'stock' }) : undefined },
          pile(v.stockCount),
          h('div.muted', {}, drawing ? 'Draw' : 'Stock'),
        ),
        h(
          `${drawing && top ? 'button' : 'div'}.ocean.plain`,
          { 'data-testid': 'gin-discard', onclick: drawing && top ? () => send({ type: 'draw', from: 'discard' }) : undefined },
          top ? cardFace(top, { testid: 'gin-top' }) : h('div.card.empty'),
          h('div.muted', {}, drawing && top ? `Take ${cardLabel(top)}` : 'Discard'),
        ),
      ),
      h('div.event', { 'data-testid': 'event' }, ev(v.last)),
      v.lastHand && (v.last?.kind === 'hand' || v.result) ? ginReveal(ctx, v.lastHand) : null,
      discarding ? h('div.hint', { 'data-testid': 'hint' }, one ? 'Discard it, or knock if you can.' : 'Pick a card to discard.') : null,
      picked.length ? pickTray(raw.filter((c) => picked.includes(c.id)), toggle, () => ctx.local.set(SEL, [])) : null,
      discarding && one
        ? h(
            'div.row.actions.wrap',
            { 'data-testid': 'gin-actions' },
            one === v.took
              ? h('span.muted', {}, 'You can’t throw back the card you just took.')
              : [
                  h('button.primary', { 'data-testid': 'gin-discard-sel', onclick: () => send({ type: 'discard', card: one }) }, 'Discard'),
                  v.knockable.includes(one)
                    ? h('button.primary.knock', { 'data-testid': 'gin-knock', onclick: () => send({ type: 'knock', card: one }) }, knockLabel(raw.filter((c) => c.id !== one)))
                    : null,
                ],
          )
        : null,
      picked.length && raw.length > 1
        ? h(
            'div.row.actions.mover',
            { 'data-testid': 'gin-move' },
            h('span.muted', {}, picked.length > 1 ? 'Move together:' : 'Move:'),
            ([['start', '⇤'], [-1, '◀'], [1, '▶'], ['end', '⇥']] as const).map(([m, label]) =>
              h('button', { 'data-testid': `gin-move-${m}`, 'aria-label': `Move ${m}`, onclick: () => move(m) }, label),
            ),
          )
        : null,
      v.hand
        ? sortBy === 'melds' && a
          ? h(
              'div.meld-row.hand-melds',
              { 'data-testid': 'hand' },
              a.melds.map((m, i) => group(m, m[0]?.rank === m[1]?.rank ? 'Set' : 'Run', { picked, onpick: toggle, testid: `gin-meld-${i}` })),
              a.deadwood.length ? group(a.deadwood, `Deadwood ${a.points}`, { picked, onpick: toggle, testid: 'gin-deadwood', dead: true }) : null,
            )
          : h('div.hand', { 'data-testid': 'hand' }, hand.map((c) => cardFace(c, { picked: picked.includes(c.id), onclick: () => toggle(c.id) })))
        : null,
      a
        ? h(
            'div.row.actions',
            {},
            h('span.badge', { 'data-testid': 'gin-deadwood-count' }, `Deadwood ${a.points}`),
            h('span.muted', {}, v.knockLimit === 0 ? 'Gin only' : `Knock at ${v.knockLimit} or less`),
            (['melds', 'custom'] as const).map((k) =>
              h(
                `button.link${sortBy === k ? '.on' : ''}`,
                {
                  'data-testid': `gin-sort-${k}`,
                  onclick: () => (k === 'custom' && sortBy !== 'custom' && ctx.local.set(orderKey, a.melds.flat().concat(a.deadwood).map((c) => c.id)), ctx.local.set(sortKey, k)),
                },
                k === 'melds' ? 'Group melds' : 'My order',
              ),
            ),
          )
        : null,
      h('div.muted', {}, `Hand ${v.handNo} · first to ${v.target} · ${ctx.names.map((_, s) => `${name(ctx, s)} ${v.scores[s] ?? 0}`).join(' · ')}`),
    );
  },
};
